import React, { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../../lib/utils';
import { useSize } from '../theme/hooks';

/** One bar. `value` null means the day is before tracking began (drawn as a stub, not a zero). */
export type TrendBucket = { label: string; value: number | null; prev: number | null; tip: string; sub: string };

// Catmull-Rom through the points, as in the prototype's previous-period curve.
const smooth = (p: [number, number][]) => {
  let d = `M${p[0][0].toFixed(1)},${p[0][1].toFixed(1)}`;
  for (let i = 0; i < p.length - 1; i++) {
    const p0 = p[i - 1] || p[i];
    const p1 = p[i];
    const p2 = p[i + 1];
    const p3 = p[i + 2] || p2;
    const t = 0.18;
    d += ` C${(p1[0] + (p2[0] - p0[0]) * t).toFixed(1)},${(p1[1] + (p2[1] - p0[1]) * t).toFixed(1)} ${(p2[0] - (p3[0] - p1[0]) * t).toFixed(1)},${(p2[1] - (p3[1] - p1[1]) * t).toFixed(1)} ${p2[0].toFixed(1)},${p2[1].toFixed(1)}`;
  }
  return d;
};

/**
 * Bars that fill their card, drawn in real pixels, with the previous period as a curve where it
 * was tracked, date labels, and a pill that follows the bar under the pointer (the peak otherwise).
 * Touch scrubs; the page still scrolls vertically.
 */
export const TrendPlot: React.FC<{ buckets: TrendBucket[]; label: string }> = ({ buckets, label }) => {
  const { ref, w: W, h: H } = useSize<HTMLDivElement>();
  const n = buckets.length;
  const peak = useMemo(() => buckets.reduce((best, b, i) => ((b.value ?? -1) > (buckets[best].value ?? -1) ? i : best), 0), [buckets]);
  const [sel, setSel] = useState<number | null>(null);
  const cur = sel ?? peak;
  const tipRef = useRef<HTMLDivElement>(null);
  const [tipW, setTipW] = useState(140);
  useEffect(() => setSel(null), [n]);
  useEffect(() => {
    if (tipRef.current) setTipW(tipRef.current.offsetWidth);
  }, [cur, buckets, W]);

  if (!W || !H || !n) return <div ref={ref} className="relative min-h-[230px] flex-1" />;

  const T = H < 220 ? 44 : 52;
  const B = H < 220 ? 22 : 26;
  const slot = W / n;
  const bw = Math.max(2, slot * 0.62);
  const max = Math.max(4, Math.ceil((Math.max(...buckets.map((b) => Math.max(b.value ?? 0, b.prev ?? 0))) * 1.2) / 2) * 2);
  const y = (v: number) => T + (H - T - B) * (1 - v / max);
  const cx = (i: number) => i * slot + slot / 2;
  const every = Math.max(1, Math.ceil(64 / slot));

  // The previous period is only drawn over the buckets it was tracked for (always a run at the end).
  const tracked = buckets.map((b, i) => (b.prev === null ? null : ([cx(i), y(b.prev)] as [number, number]))).filter((p): p is [number, number] => !!p);
  const curve = tracked.length > 1 ? smooth(tracked) : null;
  const b = buckets[cur];
  const top = b.value ? y(b.value) : H - B - 6;
  const half = tipW / 2 + 4;
  const tipLeft = Math.min(Math.max(cx(cur), half), W - half);

  const scrub = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse') return;
    const r = e.currentTarget.getBoundingClientRect();
    setSel(Math.max(0, Math.min(n - 1, Math.floor((e.clientX - r.left) / slot))));
  };

  return (
    <div ref={ref} className="relative min-h-[230px] flex-1 touch-pan-y" onMouseLeave={() => setSel(null)} onPointerDown={scrub} onPointerMove={scrub}>
      <svg className="absolute inset-0 block h-full w-full overflow-visible" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label}>
        <defs>
          <linearGradient id="oa-trend-curve" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" style={{ stopColor: 'var(--oa-text)', stopOpacity: 0.25 }} />
            <stop offset=".3" style={{ stopColor: 'var(--oa-text)', stopOpacity: 1 }} />
            <stop offset="1" style={{ stopColor: 'var(--oa-text)', stopOpacity: 0.6 }} />
          </linearGradient>
        </defs>
        {[0.5, 1].map((f) => (
          <line key={f} className="oa-guide" x1={0} x2={W} y1={y(max * f)} y2={y(max * f)} />
        ))}
        {buckets.map((bk, i) => {
          const on = i === cur;
          if (bk.value === null) return <rect key={i} x={cx(i) - bw / 2} width={bw} y={H - B - 2} height={2} rx={1} style={{ fill: 'var(--oa-line-2)' }} />;
          const h = bk.value > 0 ? Math.max(3, (bk.value / max) * (H - T - B)) : 0;
          return (
            <rect
              key={i}
              className="oa-seg-rect"
              x={cx(i) - bw / 2}
              width={bw}
              rx={Math.min(3, bw / 3)}
              y={H - B - h}
              height={h}
              style={{ ['--i' as string]: (i * (30 / n) * 0.55).toFixed(2), y: H - B - h, height: h, fill: 'var(--oa-accent)', opacity: on ? 1 : 0.42, filter: on ? 'drop-shadow(0 8px 22px var(--oa-accent-soft))' : undefined } as React.CSSProperties}
            />
          );
        })}
        {buckets.map((bk, i) => {
          const show = (n - 1 - i) % every === 0 && cx(i) > 26;
          return show ? (
            <text key={`t${i}`} className={cn('oa-axis', i === cur && 'is-on')} x={i === n - 1 ? W : cx(i)} y={H - 6} textAnchor={i === n - 1 ? 'end' : 'middle'}>
              {bk.label.replace('w/c ', '')}
            </text>
          ) : null;
        })}
        {curve ? <path className="oa-curve" d={curve} stroke="url(#oa-trend-curve)" pathLength={1} style={{ d: `path("${curve}")`, transition: 'd .6s var(--oa-ease)' } as React.CSSProperties} /> : null}
        {buckets.map((bk, i) => (
          <rect key={`h${i}`} x={i * slot} y={0} width={slot} height={H} fill="transparent" tabIndex={0} className="cursor-pointer outline-none" aria-label={`${bk.label}: ${bk.tip}`} onMouseEnter={() => setSel(i)} onFocus={() => setSel(i)} />
        ))}
      </svg>
      <div ref={tipRef} className="oa-tip" style={{ left: tipLeft, top }}>
        {b.tip}
        <small>{b.sub}</small>
      </div>
    </div>
  );
};
